/* eslint-disable no-unused-vars */
import { AnyAction, Dispatch } from 'redux'
import { signMessageToken, SupportedWallets } from 'constants/wallet'
import { getProvider } from 'blockchain/base'
import { Deferrable } from '@ethersproject/properties'
import { TransactionRequest } from '@ethersproject/abstract-provider'
import { setAddress, setChainID } from 'store/wallet/walletSlice'

import metamask from './metamask'
import binance from './binance'
import walletConnect from './wallet-connect'
import { getChainId, getProxy, getWalletType, setWalletType } from './wallet'
import { accessToken, setAccessToken } from 'utils/sendRequest'
import { loginExternalWallet } from 'services/auth'
import { getSampleChain, SupportedChainId } from 'constants/chain'
import { getUser, setUser } from 'store/user/storage'
import { setDataUser } from 'store/user/userSlice'
import { hexadecimalToNumber } from './utils'

export async function getAddress() {
  const type = getWalletType()

  if (type === SupportedWallets.ethereum) return await metamask.getAddress()
  if (type === SupportedWallets.binance) return await binance.getAddress()
  if (type === SupportedWallets.walletconnect)
    return await walletConnect.getAddress()

  return undefined
}

export async function getNetwork() {
  const type = getWalletType()

  if (type === SupportedWallets.ethereum) return await metamask.getNetwork()
  if (type === SupportedWallets.binance) return await binance.getNetwork()
  if (type === SupportedWallets.walletconnect)
    return await walletConnect.getNetwork()

  return undefined
}

export async function requestConnectToDApp(type: SupportedWallets) {
  setWalletType(type)

  if (type === SupportedWallets.ethereum) return await metamask.enable()
  if (type === SupportedWallets.binance) return await binance.enable()
  if (type === SupportedWallets.walletconnect)
    return await walletConnect.enable()

  return undefined
}

export async function requestDisconnectDApp() {
  const type = getWalletType()
  setWalletType(undefined)

  // if (type === SupportedWallets.ethereum) return await metamask
  // if (type === SupportedWallets.binance) return await binance
  if (type === SupportedWallets.walletconnect)
    return await walletConnect.disable()
}

export async function isLoggedIn(): Promise<boolean | undefined> {
  const type = getWalletType()
  if (!type) return undefined

  if (type === SupportedWallets.ethereum) return await metamask.isLoggedIn()
  if (type === SupportedWallets.binance) return await binance.isLoggedIn()
  if (type === SupportedWallets.walletconnect)
    return await walletConnect.isLoggedIn()

  return undefined
}

export async function signMessage(message: string) {
  const type = getWalletType()
  if (typeof getProxy(type) === 'undefined') return undefined

  const provider = getProvider()
  const accounts = await provider.listAccounts()
  if (!accounts.length) return undefined

  const signer = provider.getSigner()
  return await signer.signMessage(message)
}

export async function getBalance() {
  if (typeof getProxy(getWalletType()) === 'undefined' || !(await isLoggedIn()))
    return undefined

  const provider = getProvider()
  const accounts = await provider.listAccounts()
  if (!accounts.length) return undefined

  const signer = provider.getSigner()
  return await signer.getBalance()
}

export async function sendTransaction(
  transaction: Deferrable<TransactionRequest>
) {
  if (
    typeof getProxy(getWalletType()) === 'undefined' ||
    typeof transaction === 'undefined' ||
    !(await isLoggedIn())
  ) {
    return undefined
  }

  const provider = getProvider()
  const accounts = await provider.listAccounts()
  if (!accounts.length) return undefined

  if (typeof transaction.from === 'undefined') transaction.from = accounts[0]

  const signer = provider.getSigner()
  return signer.sendTransaction(transaction)
}

export async function switchChain(chainID: number | string | SupportedChainId) {
  const type = getWalletType()

  const sampleChain = getSampleChain(chainID)

  if (type === SupportedWallets.ethereum)
    return await metamask.switchChain(chainID, sampleChain)
  if (type === SupportedWallets.walletconnect)
    return await walletConnect.switchChain(chainID, sampleChain)

  return undefined
}

/* Event */

export function onChainChanged(callback: (chainId?: string) => void) {
  const type = getWalletType()

  if (type === SupportedWallets.ethereum) metamask.onChainChanged(callback)
  if (type === SupportedWallets.binance) binance.onChainChanged(callback)
  if (type === SupportedWallets.walletconnect)
    walletConnect.onChainChanged(callback)
}

export function onAccountsChanged(
  callback: (accounts?: Array<string>) => void
) {
  const type = getWalletType()

  if (type === SupportedWallets.ethereum) metamask.onAccountsChanged(callback)
  if (type === SupportedWallets.binance) binance.onAccountsChanged(callback)
  if (type === SupportedWallets.walletconnect)
    walletConnect.onAccountsChanged(callback)
}

/* ===== */

export async function loadUser(dispatch: Dispatch<AnyAction>) {
  const walletType = getWalletType()

  // Required because Wallet Connect does not connect to the DApp by itself
  if (walletType === SupportedWallets.walletconnect) {
    await requestConnectToDApp(SupportedWallets.walletconnect)
  }

  // If you are not logged in with your wallet, you will log out
  await new Promise((resolve) => setTimeout(() => resolve(true), 500))
  if (!walletType || !(await isLoggedIn()) || !(await getAddress())) {
    setAccessToken(undefined)
    setUser(undefined)
    dispatch(setDataUser(undefined))

    setWalletType(undefined)

    return
  }

  if (!!accessToken() && !!getUser()) {
    return
  }

  const signature = (await signMessage(signMessageToken).catch(() => {})) as
    | string
    | undefined

  const address = await getAddress()

  const rest = await loginExternalWallet({
    address,
    signature,
  }).catch(() => {})

  if (!rest || !(rest.status >= 200 && rest.status <= 299)) {
    return requestDisconnectDApp()
  }

  setAccessToken(rest.data.access_token)
  setUser(rest.data.user)
  dispatch(setDataUser(rest.data.user))
}

export async function loadWallet(dispatch: Dispatch<AnyAction>) {
  await getAddress().then((address) => dispatch(setAddress(address)))

  await getNetwork().then(async (network) => {
    const chainId = getChainId()
    if (!network?.chainId || network.chainId === chainId) return

    if (chainId) {
      const result = await switchChain(chainId).catch(() => {})
      dispatch(setChainID(result ? chainId : network?.chainId))
    } else {
      dispatch(setChainID(network?.chainId))
    }
  })
}

export function loadEventWallet(dispatch: Dispatch<AnyAction>) {
  const walletType = getWalletType()
  if (!walletType) return

  onAccountsChanged(async (accounts) => {
    if (
      accounts &&
      String(accounts[0]).toUpperCase() ===
        String(getUser()?.userWallet.address).toUpperCase()
    )
      return

    dispatch(setAddress(undefined))
    setAccessToken(undefined)
    setUser(undefined)
    dispatch(setDataUser(undefined))

    const address = (await getAddress().catch(() => {})) as string | undefined

    const signature = (await signMessage(signMessageToken).catch(() => {})) as
      | string
      | undefined

    const rest = await loginExternalWallet({ address, signature }).catch(
      () => {}
    )
    if (!rest || !(rest.status >= 200 && rest.status <= 299)) {
      return requestDisconnectDApp()
    }

    const result = !!accounts && accounts.length > 0 ? accounts[0] : undefined

    setAccessToken(rest.data.access_token)
    dispatch(setAddress(result))
    setUser(rest.data.user)
    dispatch(setDataUser(rest.data.user))

    // I need to reload the DApp when done
    if (typeof window === 'undefined') return
    window.location.reload()
  })

  onChainChanged((chainId) => {
    const result =
      typeof chainId !== 'undefined' ? hexadecimalToNumber(chainId) : undefined
    dispatch(setChainID(result))
  })
}
